import React from 'react';
import {
  ArrowRight, ArrowLeft, Loader2, CheckCircle2, XCircle,
  Sparkles, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const STEPS = ['Upload', 'Map Accounts', 'Validate', 'Confirm'];

export function WizardSteps({
  activeImport, step, setStep, processing, accounts,
  validationResult, setValidationResult,
  onMapAccounts, onSaveMappings, onValidate, onConfirm,
  onUpdateMapping, onCancel,
}) {
  return (
    <div className="space-y-4" data-testid="historical-import-wizard">
      {/* Step indicators */}
      <div className="flex items-center gap-1 mb-4">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              i === step ? 'bg-[#D94E18] text-white' :
              i < step ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-400'
            }`} data-testid={`step-${s.toLowerCase().replace(' ', '-')}`}>
              {i < step ? <CheckCircle2 className="w-3 h-3" /> : null}
              {s}
            </div>
            {i < STEPS.length - 1 && <ArrowRight className="w-3 h-3 text-zinc-300 flex-shrink-0" />}
          </React.Fragment>
        ))}
        <Button variant="ghost" size="sm" className="ml-auto text-zinc-400 text-xs" onClick={onCancel} data-testid="cancel-wizard-btn">Cancel</Button>
      </div>

      {/* Step 1: Map Accounts */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#D94E18]" /> AI Account Mapping</CardTitle>
            <CardDescription>{activeImport.parsed_rows} accounts parsed from "{activeImport.filename}". Click "Run AI Mapping" to auto-match accounts. Costs 5 credits.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[350px] overflow-y-auto mb-4">
              {activeImport.mappings.map((m, i) => (
                <div key={m.source_name || `mapping-${i}`} className="flex items-center gap-3 p-2 border border-zinc-100 rounded-lg" data-testid={`mapping-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-800 truncate">{m.source_name}</p>
                    <p className="text-xs text-zinc-400">Dr: {m.debit.toLocaleString()} / Cr: {m.credit.toLocaleString()}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-300 flex-shrink-0" />
                  <div className="w-48 flex-shrink-0">
                    {m.mapped_account_name ? (
                      <Badge variant="outline" className="text-emerald-700 border-emerald-200 text-xs">{m.mapped_account_code} - {m.mapped_account_name}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-200 text-xs">Not mapped</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={onMapAccounts} disabled={processing} className="bg-[#D94E18] hover:bg-[#C2410C] text-white" data-testid="run-ai-mapping-btn">
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}Run AI Mapping
              </Button>
              <Button variant="outline" onClick={() => setStep(2)} data-testid="skip-to-review-btn">Skip to Review <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Review & Edit Mappings */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Review Account Mappings</CardTitle>
            <CardDescription>Verify and correct the AI-suggested mappings before posting.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto mb-4">
              {activeImport.mappings.map((m, i) => (
                <div key={m.source_name || `review-${i}`} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 p-3 border border-zinc-100 rounded-lg" data-testid={`review-mapping-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-800">{m.source_name}</p>
                    <p className="text-xs text-zinc-400">
                      Dr: {m.debit.toLocaleString()} / Cr: {m.credit.toLocaleString()}
                      {m.ai_confidence != null && (
                        <span className={`ml-2 ${m.ai_confidence >= 0.8 ? 'text-emerald-600' : m.ai_confidence >= 0.5 ? 'text-amber-500' : 'text-red-500'}`}>
                          ({Math.round(m.ai_confidence * 100)}% confidence)
                        </span>
                      )}
                    </p>
                  </div>
                  <select value={m.mapped_account_id || ''} onChange={(e) => onUpdateMapping(i, e.target.value)}
                    className={`w-full sm:w-56 h-8 rounded-md border px-2 text-xs ${m.mapped_account_id ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}
                    data-testid={`account-select-${i}`}>
                    <option value="">-- Select Account --</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
                  </select>
                </div>
              ))}
            </div>

            {validationResult && !validationResult.valid && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="validation-errors">
                <p className="text-sm font-medium text-red-800 mb-1">Validation Issues:</p>
                {validationResult.errors.map((err, idx) => (
                  <p key={err} className="text-xs text-red-600 flex items-start gap-1.5"><XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {err}</p>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-mapping-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button variant="outline" onClick={onSaveMappings} disabled={processing} data-testid="save-mappings-btn">
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}Save Mappings
              </Button>
              <Button onClick={onValidate} disabled={processing} className="bg-[#D94E18] hover:bg-[#C2410C] text-white" data-testid="validate-btn">
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}Validate
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Ready to Post</CardTitle>
            <CardDescription>Opening balances for FY {activeImport.fiscal_year} will be posted as a journal entry dated {activeImport.fiscal_year + 1}-01-01.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-zinc-500 text-xs">Total Debits</p><p className="font-mono font-semibold text-zinc-900">{activeImport.total_debits.toLocaleString()}</p></div>
                <div><p className="text-zinc-500 text-xs">Total Credits</p><p className="font-mono font-semibold text-zinc-900">{activeImport.total_credits.toLocaleString()}</p></div>
                <div><p className="text-zinc-500 text-xs">Accounts</p><p className="font-semibold">{activeImport.mappings.length}</p></div>
                <div><p className="text-zinc-500 text-xs">Fiscal Year</p><p className="font-semibold">{activeImport.fiscal_year}</p></div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="back-to-review-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Review</Button>
              <Button onClick={onConfirm} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-import-btn">
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}Post Opening Balances
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
